<script lang="ts">
  import type { Snippet } from 'svelte';
  import { cn } from '../../lib/utils/cn';
  import Icon from '../../lib/icons/Icon.svelte';

  interface Props {
    open?: boolean;
    title: string;
    description?: string;
    class?: string;
    closable?: boolean;
    presentation?: 'dialog' | 'sheet';
    onclose?: (() => void) | undefined;
    children?: Snippet;
    footer?: Snippet;
  }

  let {
    open = $bindable(false),
    title,
    description = '',
    class: className = '',
    closable = false,
    presentation = 'dialog',
    onclose,
    children,
    footer,
  }: Props = $props();

  let dialogEl = $state<HTMLDivElement | null>(null);
  let restoreFocusEl: HTMLElement | null = null;

  const FOCUSABLE =
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function close() {
    open = false;
    onclose?.();
    queueMicrotask(() => {
      restoreFocusEl?.focus();
      restoreFocusEl = null;
    });
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      close();
    }

    if (event.key === 'Tab' && open && dialogEl) {
      const focusable = [...dialogEl.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (element) => element.offsetParent !== null,
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (event.shiftKey && active === first && last) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last && first) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  function handleBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      close();
    }
  }

  $effect(() => {
    if (!open) return;

    restoreFocusEl = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    queueMicrotask(() => {
      if (!dialogEl) return;
      const focusable = dialogEl.querySelector<HTMLElement>(FOCUSABLE);
      (focusable ?? dialogEl).focus();
    });
  });
</script>

<svelte:window onkeydown={handleKeydown} />

{#if open}
  <div
    class="pointer-events-auto fixed inset-0 z-50 flex items-center justify-center bg-[rgba(13,58,36,0.55)] p-4"
    class:dialog-backdrop--sheet={presentation === 'sheet'}
    role="presentation"
    onclick={handleBackdropClick}
  >
    <div
      bind:this={dialogEl}
      class={cn(
        'w-full max-w-md rounded-[var(--tx42-radius-xl)] border border-[var(--tx42-panel-border-subtle)] bg-tx42-panel p-6 shadow-tx42-panel outline-none',
        className,
      )}
      class:dialog--sheet={presentation === 'sheet'}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog-title"
      aria-describedby={description ? 'dialog-description' : undefined}
      tabindex="-1"
    >
      <div class="flex items-start justify-between gap-4">
        <h2 id="dialog-title" class="tx42-text-display text-xl text-tx42-text">{title}</h2>
        {#if closable}
          <button
            type="button"
            class="dialog__close tx42-interactive"
            aria-label="Close dialog"
            data-testid="dialog-close"
            onclick={close}
          >
            <Icon name="xMark" size="lg" />
          </button>
        {/if}
      </div>
      {#if description}
        <p id="dialog-description" class="mt-2 text-sm text-tx42-text-muted">{description}</p>
      {/if}
      {#if children}
        <div class="mt-4">
          {@render children()}
        </div>
      {/if}
      {#if footer}
        <div class="mt-6 flex justify-end gap-3">
          {@render footer()}
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
  .dialog--sheet { max-height: 90dvh; overflow-y: auto; overscroll-behavior: contain; }
  .dialog-backdrop--sheet { background: rgb(0 0 0 / 45%); }
  @media (max-width: 600px) {
    .dialog-backdrop--sheet { align-items: flex-end; padding: 0; }
    .dialog--sheet { max-width: none; padding: 24px 24px max(20px, env(safe-area-inset-bottom)); border-radius: 24px 24px 0 0; border-bottom: 0; }
  }

  .dialog__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 2.25rem;
    min-height: 2.25rem;
    margin: -0.35rem -0.35rem 0 0;
    border: 0;
    border-radius: var(--tx42-radius-pill);
    background: transparent;
    color: var(--tx42-text-muted);
  }

  .dialog__close:hover {
    background: rgb(0 0 0 / 8%);
    color: var(--tx42-text);
  }
</style>
